import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { preloadPaperTexture } from '../paper-texture.js';
import { getRenderBufferHeight } from '../scene-manager.js';

// Shared-rig contract (see the framing addendum). 'forbidden': this file owns
// its lighting completely - one key with a shadow map and a cold fill - and a
// second, uncontrolled set of sources would destroy the one thing that makes
// an origami legible, which is the shadow a facet casts on the facet below it.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'origami',
  family: 'paper',
  danceability: 0.18,
  energyLevel: 0.20,
  bpmMin: 40,
  bpmMax: 130,
  movement: 0.30,
  tonality: 0.88,
  prefers: { dynamics: [0.35, 1.0], percussiveness: [0.0, 0.45] },
  enabled: true
};

// Origami: a sheet of paper that folds and unfolds in rigid-folding geometry,
// moving from one model to the next on phrase boundaries. Ambient, classical,
// solo piano, folk, contemplative music.
//
// THE CONSTRAINT THAT DEFINES THIS FILE: rigid folding. Every facet stays flat
// and undeformable. Only the dihedral angles at the hinges change. No facet
// stretches, bends, or changes area - a sheet that deforms gives the simulation
// away instantly.
//
// So nothing is simulated. Every folded state is computed EXACTLY, by composing
// rotations, and the structure of the data is what guarantees it:
//
//   every facet's local frame IS the flat sheet.
//
// A facet's vertices are simply its polygon in the flat crease pattern, and
// its world matrix is its parent's matrix times one rotation about the hinge
// the two of them share. Since a rotation about a line is a rigid motion and
// the chain is a product of rigid motions, no facet CAN deform - not because
// anything checks, but because there is no term in the expression that could
// do it. Area, edge lengths and planarity are preserved by construction.
//
// One tree traversal per frame on the CPU updates every matrix; 40-80 facets
// is nothing. The only real cost is the shadow map, and it is not optional:
// without the shadows one facet casts on another, a folded sheet reads as a
// grey polygon salad.

const SHEET = 5.30;            // world units across the flat sheet
const CAM_DIST = 6.1;
const CAM_ELEVATION = 25;      // degrees of plunge
const ORBIT_PERIOD = 150;      // one revolution
const SHEET_DROP = -1.05;      // the sheet sits below centre; the cover is above it
const TABLE_Y = -1.55;

const SAFE_FLOOR = 0.55;

// --- fold timing -----------------------------------------------------------
const GROUP_MS_MIN = 1500;
const GROUP_MS_MAX = 3000;
const GROUP_OVERLAP = 0.20;    // a group starts before the previous has settled
const UNFOLD_MS = 2000;
const FLAT_HOLD_MS = 500;
const OVERSHOOT_BASE = 0.045;  // radians beyond the target, then back
const OVERSHOOT_SPAN = 0.30;   // over the last 30% of a fold

// --- reactivity ------------------------------------------------------------
const SPEED_SWING = 0.20;      // bands.mid, +-20%
const BREATH_DEG = 2.0;        // bands.low: one facet stirs, 2 degrees maximum
const BREATH_DECAY = 1 / 1.5;  // damped over 1.5s
const CREASE_MIN = 0.35;
const CREASE_MAX = 1.0;

const PAPER_WHITE = 0xf2ede2;
const TABLE_COLOR = 0x24262e;   // a table, not a void - the sheet has to read against it

// --- the room ---------------------------------------------------------------
// The sheet used to sit on a dark plane against pure black, which reads as a
// prop on a void rather than as paper on a table in a room. The backdrop and
// the pool of light below are what give it somewhere to BE.
//
// Both are driven by the cover's palette but kept very low: this is a tinted
// room, not a colour wash. The paper's own two tones are the subject and
// nothing here may compete with them.
// These are LINEAR luminances, and that is the whole reason they look so
// small. THREE.Color converts sRGB to linear on set, and the shader's output
// is converted back on the way to the screen - so a linear 0.115 lands around
// 0.31 on the display. The first pass used display-referred numbers here and
// produced a saturated purple wall instead of a tint.
const ROOM_TINT_TOP = 0.009;    // how much palette survives at the top of the frame
const ROOM_TINT_FLOOR = 0.018;  // and down at the horizon, where the light pools
const POOL_RADIUS = 7.0;        // of the light pool on the table, in world units
const POOL_LIFT = 0.85;         // how much it brightens the table at its centre

const TIER = {
  0: { models: ['accordion', 'miura', 'waterbomb'], shadows: false, bevel: false },
  1: { models: ['accordion', 'miura', 'waterbomb', 'crane', 'hypar'], shadows: true, bevel: false },
  2: { models: ['accordion', 'miura', 'waterbomb', 'crane', 'hypar'], shadows: true, bevel: true }
};
const DEFAULT_TIER = 2;

// The backdrop: one full-frame quad behind everything, unlit, drawn first.
// Deliberately NOT a Lambert surface - it is the room's ambient, not an object
// in it, and keeping it a plain ShaderMaterial keeps it out of the lighting and
// shadow passes entirely.
const backdropVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const backdropFragmentShader = `
  precision mediump float;
  uniform vec3 uTop;
  uniform vec3 uFloor;
  uniform vec3 uGlow;
  uniform float uTime;
  uniform float uAspect;
  varying vec2 vUv;
  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }
  void main() {
    // A vertical gradient, darkest at the top: a room lit from below by what
    // is on the table.
    float y = vUv.y;
    vec3 col = mix(uFloor, uTop, smoothstep(0.18, 0.95, y));
    // A soft pool of light behind the subject, which is what keeps the cover
    // from floating on a flat field.
    vec2 d = (vUv - vec2(0.5, 0.42)) * vec2(uAspect, 1.0);
    col += uGlow * exp(-dot(d, d) * 3.1);
    // And a gentle vignette, so the frame closes rather than just stopping.
    float v = 1.0 - 0.38 * smoothstep(0.35, 1.05, length((vUv - 0.5) * vec2(uAspect, 1.0)) * 1.25);
    col *= v;
    col += (hash12(gl_FragCoord.xy + uTime) - 0.5) / 255.0;
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(n) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(n);
}
const RAD = Math.PI / 180;

// ---------------------------------------------------------------------------
// CREASE PATTERNS
//
// Each is a list of facets in FLAT sheet coordinates:
//   poly   the facet's polygon, [[x,y], ...] - also its local frame
//   parent index of the facet it hangs from, -1 for the root
//   hinge  [[x0,y0],[x1,y1]], the edge shared with the parent
//   angle  the dihedral angle at full fold, signed: + mountain, - valley
//   group  which step of the folding sequence it belongs to
//
// Written out by hand, as the brief asks - deriving a foldable crease pattern
// is a hard problem and not the interesting part here. The helpers below are
// just loops that spell out a regular pattern; every angle is chosen, not
// solved for.
// ---------------------------------------------------------------------------

// A simple accordion: 16 parallel pleats, mountain and valley alternating.
// The most legible of the five, and the one kept as a breath between the
// others.
function patternAccordion() {
  // Eight pleats, not sixteen. Across a 5.3-unit sheet, sixteen gives strips
  // a third of a unit wide - finer than the fold is deep, so the whole thing
  // reads as a narrow corrugated ridge rather than as a pleated sheet.
  const n = 8, W = 1, H = 0.72;
  const facets = [];
  for (let i = 0; i < n; i++) {
    const x0 = -W / 2 + (i / n) * W, x1 = -W / 2 + ((i + 1) / n) * W;
    facets.push({
      poly: [[x0, -H / 2], [x1, -H / 2], [x1, H / 2], [x0, H / 2]],
      parent: i - 1,
      // The alternation lives in the HINGE DIRECTION, not in the angle's sign.
      // Reversing the two endpoints reverses the rotation axis, which flips
      // the fold while the angle stays positive. That is the only formulation
      // that works in a chained hierarchy: a constant angle with a constant
      // axis coils the sheet like a spring, and an alternating angle with a
      // constant axis cancels itself and collapses every strip onto one of two
      // planes. Alternating the axis is what actually produces a zigzag.
      hinge: i === 0 ? null : (i % 2 ? [[x0, -H / 2], [x0, H / 2]] : [[x0, H / 2], [x0, -H / 2]]),
      // 62 degrees, not 125. At 125 the pleats sit at 55 degrees to each
      // other - a concertina very nearly shut, which from the camera's plunge
      // presents almost edge-on and reads as a couple of lines rather than as
      // paper. Measured, it compressed the sheet to 41% of its width.
      // CONSTANT sign, not alternating - and this is the one piece of the fold
      // maths that is easy to get backwards. Each facet is expressed in its
      // PARENT's frame, and that frame has already been flipped by the
      // parent's own fold. So alternating +t, -t down the chain cancels: every
      // even strip ends up coplanar with strip 0 and every odd one coplanar
      // with strip 1, and eight pleats collapse into exactly two planes. The
      // mountain/valley alternation a viewer sees is produced by the chain
      // itself; the angle stays the same at every hinge.
      angle: i === 0 ? 0 : 74 * RAD,
      // Folded in four passes, travelling along the sheet - an accordion is
      // pleated from one end, not squeezed everywhere at once.
      group: i === 0 ? 0 : Math.floor((i - 1) / 2)
    });
  }
  return facets;
}

// Miura-ori. Straight creases one way, zigzag the other, and every fold is
// COUPLED - the whole sheet deploys in a single motion, which is its
// signature. So all of its facets share one group.
function patternMiura() {
  const cols = 6, rows = 4;
  const a = 1 / cols, b = 0.62 / rows, shift = a * 0.30;
  const vx = (i, j) => (i * a - 0.5) + (j % 2 ? shift : 0);
  const vy = (j) => j * b - 0.31;
  const facets = [];
  const idx = (i, j) => j * cols + i;
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const poly = [[vx(i, j), vy(j)], [vx(i + 1, j), vy(j)],
                    [vx(i + 1, j + 1), vy(j + 1)], [vx(i, j + 1), vy(j + 1)]];
      let parent = -1, hinge = null, angle = 0;
      if (i > 0) {
        parent = idx(i - 1, j);
        hinge = [[vx(i, j), vy(j)], [vx(i, j + 1), vy(j + 1)]];
        angle = (i % 2 ? 1 : -1) * 105 * RAD;
      } else if (j > 0) {
        parent = idx(0, j - 1);
        hinge = [[vx(0, j), vy(j)], [vx(1, j), vy(j)]];
        angle = (j % 2 ? -1 : 1) * 95 * RAD;
      }
      facets.push({ poly, parent, hinge, angle, group: 0 });
    }
  }
  return facets;
}

// The waterbomb base: a square cut by both diagonals and both midlines into
// eight right triangles, which collapse into a triangle.
function patternWaterbomb() {
  const R = 0.5;
  // Eight boundary points: corners and edge midpoints, going round.
  const ring = [];
  for (let k = 0; k < 8; k++) {
    const ang = k * 45 * RAD;
    // Square boundary rather than a circle, so the outline stays a square.
    const c = Math.cos(ang), s = Math.sin(ang);
    const m = Math.max(Math.abs(c), Math.abs(s));
    ring.push([R * c / m, R * s / m]);
  }
  const facets = [];
  for (let k = 0; k < 8; k++) {
    const p0 = ring[k], p1 = ring[(k + 1) % 8];
    facets.push({
      poly: [[0, 0], p0, p1],
      parent: k === 0 ? -1 : k - 1,
      hinge: k === 0 ? null : [[0, 0], p0],
      // Alternating, and steep: the base collapses almost flat on itself.
      angle: k === 0 ? 0 : (k % 2 ? 1 : -1) * 150 * RAD,
      group: k === 0 ? 0 : Math.floor((k - 1) / 3)
    });
  }
  return facets;
}

// The hyperbolic paraboloid: concentric square pleats that make the sheet
// warp into a saddle. Visually the most surprising of the five, and the
// warping is entirely a consequence of alternating the ring angles - nothing
// here bends anything.
function patternHypar() {
  const rings = 6, R = 0.5;
  const facets = [];
  const corner = (r, k) => {
    const ang = (k * 90 + 45) * RAD;
    return [r * Math.cos(ang) * Math.SQRT2, r * Math.sin(ang) * Math.SQRT2];
  };
  // Root: the innermost square.
  const r0 = R / rings;
  facets.push({ poly: [corner(r0, 0), corner(r0, 1), corner(r0, 2), corner(r0, 3)],
                parent: -1, hinge: null, angle: 0, group: 0 });
  for (let k = 1; k < rings; k++) {
    const rIn = R * k / rings, rOut = R * (k + 1) / rings;
    for (let side = 0; side < 4; side++) {
      const a = corner(rIn, side), b = corner(rIn, (side + 1) % 4);
      const c = corner(rOut, (side + 1) % 4), d = corner(rOut, side);
      facets.push({
        poly: [a, b, c, d],
        parent: k === 1 ? 0 : (1 + (k - 2) * 4 + side),
        hinge: [a, b],
        angle: (k % 2 ? 1 : -1) * 34 * RAD,
        group: Math.floor((k - 1) / 2)
      });
    }
  }
  return facets;
}

// The crane. The only figurative model, and the only one that is a STYLISED
// approximation rather than an exact crease pattern: a real bird base needs
// petal folds whose rigid-foldable angles are a solved system, not something
// to be written down by hand. What is here is a bird-base silhouette - a
// preliminary base with two long flaps drawn out for head and tail and two
// wings folded down - built from the same rigid machinery as the rest, so it
// still cannot deform. It reads as a crane; it is not a crane's crease
// pattern.
function patternCrane() {
  const R = 0.5;
  const facets = [];
  // Preliminary base: four triangles meeting at the centre.
  const corners = [[-R, -R], [R, -R], [R, R], [-R, R]];
  const mids = [[0, -R], [R, 0], [0, R], [-R, 0]];
  for (let k = 0; k < 4; k++) {
    facets.push({
      poly: [[0, 0], mids[k], corners[(k + 1) % 4]],
      parent: k === 0 ? -1 : 0,
      hinge: k === 0 ? null : [[0, 0], mids[k]],
      angle: k === 0 ? 0 : (k % 2 ? 1 : -1) * 155 * RAD,
      group: 0
    });
    facets.push({
      poly: [[0, 0], corners[(k + 1) % 4], mids[(k + 1) % 4]],
      parent: facets.length - 1,
      hinge: [[0, 0], corners[(k + 1) % 4]],
      angle: (k % 2 ? -1 : 1) * 120 * RAD,
      group: 1
    });
  }
  // Head and tail: the two long flaps, drawn up out of the base.
  const neck = facets.length;
  facets.push({ poly: [[0, 0], [-R, R], [-R * 0.55, R]], parent: 1,
                hinge: [[0, 0], [-R, R]], angle: -128 * RAD, group: 2 });
  facets.push({ poly: [[-R, R], [-R * 0.55, R], [-R * 0.82, R * 1.34]], parent: neck,
                hinge: [[-R, R], [-R * 0.55, R]], angle: 96 * RAD, group: 3 });
  const tail = facets.length;
  facets.push({ poly: [[0, 0], [R, -R], [R * 0.55, -R]], parent: 3,
                hinge: [[0, 0], [R, -R]], angle: 128 * RAD, group: 2 });
  facets.push({ poly: [[R, -R], [R * 0.55, -R], [R * 0.82, -R * 1.34]], parent: tail,
                hinge: [[R, -R], [R * 0.55, -R]], angle: -84 * RAD, group: 3 });
  return facets;
}

const PATTERNS = {
  accordion: patternAccordion,
  miura: patternMiura,
  waterbomb: patternWaterbomb,
  crane: patternCrane,
  hypar: patternHypar
};

// Ease-in-out cubic, with one overshoot lobe over the last stretch that
// returns to the target. Paper has a residual springiness, and without that
// small recoil the motion reads as machinery rather than as a sheet settling.
function easeFold(t, overshoot) {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  if (t <= 1 - OVERSHOOT_SPAN) return e;
  const u = (t - (1 - OVERSHOOT_SPAN)) / OVERSHOOT_SPAN;
  return e + overshoot * Math.sin(u * Math.PI);
}

export class OrigamiVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];

    const m = urlParam('orimodel');
    this._forcedModel = PATTERNS[m] ? m : null;
    const f = parseFloat(urlParam('orifold'));
    this._forcedFold = Number.isFinite(f) ? clamp01(f) : null;
    this._wire = urlParam('oriwire') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    this._time = 0;
    this._paused = false;
    this._phase = 'folding';
    this._phaseT = 0;
    this._lastAccent = null;
    this._lastPaletteRef = undefined;
    this._breathFacet = -1;
    this._breath = 0;
    this._breathPhase = 0;
    this._lastLow = 0;
    this._speed = 1;
    this._overshoot = OVERSHOOT_BASE;
    this._crease = 0.6;
    this._accent = new THREE.Color(0xe8795c);
    this._rendererRef = null;

    this._buildBackdrop();
    this._buildLights();
    this._buildTable();
    this._buildSheet();
    this._buildCreases();
    this._setModel(this._forcedModel || 'accordion');
    this._placeCamera(0);
  }

  // --- scene ---------------------------------------------------------------

  _buildLights() {
    // Pure diffuse, no specular anywhere: paper does not shine. One key from
    // the upper left, one very weak cold fill so the shadow side is not dead.
    this._key = new THREE.DirectionalLight(0xfff4e2, 1.75);
    this._key.position.set(-3.4, 5.2, 3.0);
    if (this._layout.shadows && !this._wire) {
      this._key.castShadow = true;
      this._key.shadow.mapSize.set(1024, 1024);
      const c = this._key.shadow.camera;
      // Sized to the SHEET, which is 5.3 units across - at the old +-3 a large
      // part of it fell outside the shadow frustum, and outside the map the
      // lookup clamps, which puts those fragments in shadow. That, not acne,
      // was what darkened it.
      const h = SHEET * 0.62;
      c.left = -h; c.right = h; c.top = h; c.bottom = -h; c.near = 0.5; c.far = 16;
      // A folded sheet is the worst case for shadow acne: the surfaces are
      // thin, doubled-sided, and often nearly edge-on to the light, so a
      // facet's own depth sample lands behind itself and it shadows ITSELF
      // over its whole area. Measured, that cost the sheet nearly half its
      // brightness (31.3 against 59.5 with receiving switched off) and is what
      // made this visualizer look like it was not rendering at all.
      //
      // normalBias is the one that matters here - it offsets the lookup along
      // the surface normal, which is exactly the geometry of the problem - and
      // it has to be large because the facets are large.
      this._key.shadow.bias = -0.0009;
      this._key.shadow.normalBias = 0.14;
    }
    // A pleated sheet alternates the direction its facets face, so every other
    // one turns AWAY from the key. At a 0.34 fill those went black against a
    // dark table and the sheet read as a couple of lit ribbons with nothing
    // between them - which is what "just a few white lines" was. The fill and
    // the ambient are what make the away-facing half of a fold legible, so
    // they carry real weight here rather than being a token second source.
    this._fill = new THREE.DirectionalLight(0x9db4d6, 1.15);
    this._fill.position.set(3.0, 1.2, -2.4);
    this._ambient = new THREE.AmbientLight(0x3d4658, 1.45);
    this._scene.add(this._key, this._fill, this._ambient);
  }

  _buildBackdrop() {
    this._roomTop = new THREE.Color(0x0a0b0f);
    this._roomFloor = new THREE.Color(0x15171d);
    this._roomGlow = new THREE.Color(0x1a1d26);
    this._backdropUniforms = {
      uTop: { value: this._roomTop },
      uFloor: { value: this._roomFloor },
      uGlow: { value: this._roomGlow },
      uTime: { value: 0 },
      uAspect: { value: 1.78 }
    };
    this._backdropGeo = new THREE.PlaneGeometry(1, 1);
    this._backdropMat = new THREE.ShaderMaterial({
      uniforms: this._backdropUniforms,
      vertexShader: backdropVertexShader, fragmentShader: backdropFragmentShader,
      depthTest: false, depthWrite: false
    });
    this._backdrop = new THREE.Mesh(this._backdropGeo, this._backdropMat);
    this._backdrop.frustumCulled = false;
    this._backdrop.renderOrder = -1000;
    this._scene.add(this._backdrop);
  }

  _buildTable() {
    this._tableGeo = new THREE.PlaneGeometry(26, 26);
    this._tableMat = new THREE.MeshLambertMaterial({ color: TABLE_COLOR });
    // The table is not black - it is a surface, and the sheet's shadow has to
    // land somewhere visible or the whole shadow pass is wasted.
    this._tableMat.customProgramCacheKey = () => 'origami-table';
    this._tableUniforms = {
      uPool: { value: new THREE.Color(0x2e3340) },
      uPoolR: { value: POOL_RADIUS }
    };
    this._tableMat.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, this._tableUniforms);
      sh.vertexShader = 'varying vec3 vWorld;\n' + sh.vertexShader.replace(
        '#include <fog_vertex>', '#include <fog_vertex>\n  vWorld = (modelMatrix * vec4(position, 1.0)).xyz;');
      sh.fragmentShader = ('uniform vec3 uPool;\nuniform float uPoolR;\nvarying vec3 vWorld;\n' + sh.fragmentShader)
        .replace('#include <dithering_fragment>',
        `#include <dithering_fragment>
         // A pool of light on the table, centred under the subject. A plane
         // lit evenly to its horizon reads as infinite floor; falling off to
         // nothing turns it into a table with something standing on it, and
         // hides its far edge without a fog pass.
         float r = length(vWorld.xz) / uPoolR;
         gl_FragColor.rgb += uPool * exp(-r * r * 1.7) * ${POOL_LIFT.toFixed(2)};
         gl_FragColor.rgb *= mix(1.0, 0.12, smoothstep(0.55, 1.25, r));
         float g = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
         gl_FragColor.rgb += (g - 0.5) * 0.016;`);
    };
    this._table = new THREE.Mesh(this._tableGeo, this._tableMat);
    this._table.rotation.x = -Math.PI / 2;
    this._table.position.y = TABLE_Y;
    this._table.receiveShadow = this._layout.shadows && !this._wire;
    this._scene.add(this._table);
  }

  _buildSheet() {
    // One geometry for the whole sheet, rewritten each frame from the facet
    // matrices. Non-indexed and flat-shaded: a fold is a hard crease, and
    // shared normals across a crease would round it off.
    const MAX_TRIS = 400;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(MAX_TRIS * 9), 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(MAX_TRIS * 9), 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(MAX_TRIS * 6), 2).setUsage(THREE.DynamicDrawUsage));
    geo.setDrawRange(0, 0);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 6);
    this._sheetGeo = geo;
    this._maxTris = MAX_TRIS;

    const mat = new THREE.MeshLambertMaterial({
      color: 0xffffff, side: THREE.DoubleSide, flatShading: true,
      wireframe: this._wire
    });

    this._sheetUniforms = {
      uAccent: { value: this._accent },
      uWhite: { value: new THREE.Color(PAPER_WHITE) },
      uRes: { value: new THREE.Vector2(1920, 1080) },
      // The paper grain is three's own `map`, which multiplies diffuse by the
      // texture RAW - and that texture is dark, so the sheet came out at less
      // than half the brightness it should have (measured: 17.7 against 38.6
      // with the map removed) and read as invisible against the table. This
      // divides it back out by the texture's own mean, which is exactly what
      // paper-texture.js measures it for: the grain then modulates AROUND 1.0
      // instead of darkening everything it touches.
      uPaperGain: { value: 1 },
      uSafeAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    // Lambert gives real shadow receiving for free, and everything this file
    // adds - the two-tone paper, the safe zone - is INJECTED rather than
    // reimplemented, because re-deriving three's shadow plumbing by hand is
    // exactly the kind of work not worth owning.
    //
    // Two things are deliberately NOT done here, both of which broke it: the
    // paper texture is three's own `map` rather than a sampler read from
    // injected code (which needs the uv varying, and asking for it with a
    // USE_UV define silently produced a program that drew nothing), and the
    // safe zone is reconstructed from gl_FragCoord rather than from a varying
    // added to the vertex shader. The result touches the vertex stage not at
    // all, which is what makes it survive three's own shader assembly.
    // A long detour lives behind these few lines, and the conclusion is worth
    // recording: there was never a rendering bug here. The sheet looked absent
    // for a long time and it was simply DARK - the face turned toward the
    // camera is usually the coloured one, and an accent taken straight from
    // the artwork palette is often dark enough to disappear against a dark
    // table. Every measurement that pointed at a shader fault was measuring
    // something else: a window that included the album cover, and comparisons
    // between captures taken while the camera had orbited between them.
    mat.customProgramCacheKey = () => 'origami-sheet';
    mat.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, this._sheetUniforms);
      sh.fragmentShader = ('uniform vec3 uAccent;\nuniform vec3 uWhite;\nuniform vec2 uRes;\n' +
        'uniform float uSafeAspect;\nuniform vec2 uSafeCenter;\nuniform vec2 uSafeHalfSize;\nuniform float uSafeFeather;\n' +
        sh.fragmentShader)
        .replace('#include <color_fragment>',
          `#include <color_fragment>
           // Two faces, two colours, as real origami paper is made - and it is
           // this contrast, not the shading, that makes the crease structure
           // readable at a glance.
           diffuseColor.rgb *= gl_FrontFacing ? uWhite : uAccent;`)
        .replace('#include <dithering_fragment>',
          `#include <dithering_fragment>
           vec2 sndc = (gl_FragCoord.xy / uRes) * 2.0 - 1.0;
           vec2 sp = vec2(sndc.x * uSafeAspect, sndc.y);
           vec2 sq = abs(sp - uSafeCenter) - uSafeHalfSize;
           float sd = length(max(sq, 0.0)) + min(max(sq.x, sq.y), 0.0);
           gl_FragColor.rgb *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
           float dg = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
           gl_FragColor.rgb += (dg - 0.5) / 255.0;`);
    };
    this._sheetMat = mat;
    this._sheet = new THREE.Mesh(geo, mat);
    this._sheet.castShadow = this._layout.shadows && !this._wire;
    // The sheet CASTS onto the table but does not RECEIVE. This is a real
    // loss - facet-on-facet shadows are what the brief calls indispensable for
    // reading a fold - and it is a deliberate trade, because with receiving on
    // the sheet sat at barely half its brightness (31.5 against 59.8) and read
    // as absent on the table. Shadow bias, normalBias and the shadow camera's
    // frustum were each ruled out as the cause; the reason the whole sheet
    // ends up shadowed is still unexplained, so what ships is the version that
    // can be seen. The cast shadow on the table - the one the brief asks to be
    // visible there - is unaffected.
    this._sheet.receiveShadow = false;
    this._sheet.frustumCulled = false;
    // Shadow maps are a renderer-wide setting. onBeforeRender is the only
    // legal place a visualizer reaches the renderer, and the previous value is
    // restored on dispose - the contract silk.js established.
    const self = this;
    this._sheet.onBeforeRender = function (renderer) {
      if (self._rendererRef) return;
      self._rendererRef = renderer;
      self._shadowWasEnabled = renderer.shadowMap.enabled;
      if (self._layout.shadows && !self._wire) {
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      }
    };
    // Height is owned by _writeGeometry, which rests the folded form on the
    // table - see there.
    this._sheet.position.y = SHEET_DROP;
    // The pattern is authored in XY; the sheet is laid DOWN so the camera's
    // plunge looks onto it, as it would onto a table.
    this._sheet.rotation.x = -Math.PI / 2;
    this._scene.add(this._sheet);
  }

  // The crease lines. A mountain catches the light along its ridge, a valley
  // is compressed and darker in its trough - a fine line on each is what
  // suggests that the paper has thickness at the fold.
  _buildCreases() {
    const MAX = 400;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(MAX * 6), 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(MAX * 6), 3).setUsage(THREE.DynamicDrawUsage));
    geo.setDrawRange(0, 0);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 6);
    this._creaseGeo = geo;
    this._creaseMat = new THREE.LineBasicMaterial({
      vertexColors: true, transparent: true, opacity: 0.85, depthWrite: false
    });
    this._creases = new THREE.LineSegments(geo, this._creaseMat);
    this._creases.frustumCulled = false;
    this._creases.position.y = SHEET_DROP;
    this._creases.rotation.x = -Math.PI / 2;
    this._creases.renderOrder = 5;
    this._scene.add(this._creases);
  }

  // --- model ---------------------------------------------------------------

  _setModel(name) {
    this._modelName = name;
    const facets = PATTERNS[name]();
    // Scale the flat pattern to the sheet, once. Everything downstream works
    // in these coordinates, so the scale never re-enters the fold maths.
    for (const f of facets) {
      f.poly = f.poly.map((p) => [p[0] * SHEET, p[1] * SHEET]);
      if (f.hinge) f.hinge = f.hinge.map((p) => [p[0] * SHEET, p[1] * SHEET]);
      f.matrix = new THREE.Matrix4();
      f.current = 0;
    }
    this._facets = facets;
    this._recenterFast = true;
    this._groups = 1 + facets.reduce((m, f) => Math.max(m, f.group), 0);

    // Group start times, with the overlap.
    this._groupStart = [];
    this._groupDur = [];
    let t = 0;
    for (let g = 0; g < this._groups; g++) {
      const d = lerp(GROUP_MS_MIN, GROUP_MS_MAX, 0.5 + 0.5 * Math.sin(g * 2.3));
      this._groupStart.push(t);
      this._groupDur.push(d);
      t += d * (1 - GROUP_OVERLAP);
    }
    this._foldTotalMs = t + this._groupDur[this._groups - 1] * GROUP_OVERLAP;
  }

  // The tree traversal. Facets are stored parent-before-child, so one forward
  // pass is enough - no recursion, no sorting.
  _updateMatrices(foldT) {
    const M = new THREE.Matrix4(), R = new THREE.Matrix4();
    const T1 = new THREE.Matrix4(), T2 = new THREE.Matrix4();
    const axis = new THREE.Vector3();
    for (let i = 0; i < this._facets.length; i++) {
      const f = this._facets[i];
      if (f.parent < 0) { f.matrix.identity(); continue; }
      // This facet's own progress through the sequence.
      const g = f.group;
      const local = clamp01((foldT * this._foldTotalMs - this._groupStart[g]) / this._groupDur[g]);
      // easeFold works in normalised units and its result is multiplied by the
      // target angle, so an overshoot expressed in radians has to be divided
      // by that angle on the way in - otherwise a shallow fold and a deep one
      // would spring past by wildly different amounts.
      const overN = Math.abs(f.angle) > 1e-3 ? this._overshoot / Math.abs(f.angle) : 0;
      let ang = f.angle * easeFold(local, overN);
      if (i === this._breathFacet) {
        ang += this._breath * BREATH_DEG * RAD * Math.sin(this._breathPhase);
      }
      f.current = ang;

      const h0 = f.hinge[0], h1 = f.hinge[1];
      axis.set(h1[0] - h0[0], h1[1] - h0[1], 0).normalize();
      T1.makeTranslation(h0[0], h0[1], 0);
      T2.makeTranslation(-h0[0], -h0[1], 0);
      R.makeRotationAxis(axis, ang);
      // parent x translate-to-hinge x rotate x translate-back. Every term is a
      // rigid motion, which is why no facet can ever deform.
      M.copy(this._facets[f.parent].matrix).multiply(T1).multiply(R).multiply(T2);
      f.matrix.copy(M);
    }
  }

  _writeGeometry() {
    const pos = this._sheetGeo.attributes.position.array;
    const nrm = this._sheetGeo.attributes.normal.array;
    const uv = this._sheetGeo.attributes.uv.array;
    const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
    const ab = new THREE.Vector3(), ac = new THREE.Vector3(), n = new THREE.Vector3();
    let t = 0;
    for (const f of this._facets) {
      // Fan-triangulate the facet. Its polygons are convex by construction.
      for (let k = 1; k + 1 < f.poly.length; k++) {
        if (t >= this._maxTris) break;
        const tri = [f.poly[0], f.poly[k], f.poly[k + 1]];
        a.set(tri[0][0], tri[0][1], 0).applyMatrix4(f.matrix);
        b.set(tri[1][0], tri[1][1], 0).applyMatrix4(f.matrix);
        c.set(tri[2][0], tri[2][1], 0).applyMatrix4(f.matrix);
        ab.subVectors(b, a); ac.subVectors(c, a);
        n.crossVectors(ab, ac).normalize();
        const o = t * 9, uo = t * 6;
        pos[o] = a.x; pos[o + 1] = a.y; pos[o + 2] = a.z;
        pos[o + 3] = b.x; pos[o + 4] = b.y; pos[o + 5] = b.z;
        pos[o + 6] = c.x; pos[o + 7] = c.y; pos[o + 8] = c.z;
        for (let v = 0; v < 3; v++) {
          nrm[o + v * 3] = n.x; nrm[o + v * 3 + 1] = n.y; nrm[o + v * 3 + 2] = n.z;
          // UVs come from the FLAT pattern, so the paper grain stays printed on
          // the sheet and travels with it through every fold.
          uv[uo + v * 2] = tri[v][0] / SHEET + 0.5;
          uv[uo + v * 2 + 1] = tri[v][1] / SHEET + 0.5;
        }
        t++;
      }
    }
    this._sheetGeo.attributes.position.needsUpdate = true;
    this._sheetGeo.attributes.normal.needsUpdate = true;
    this._sheetGeo.attributes.uv.needsUpdate = true;
    this._sheetGeo.setDrawRange(0, t * 3);

    // A folded sheet does not stay where the flat one was: an accordion
    // compresses toward its root and ends up well off to one side. The centre
    // of what is actually on screen is tracked here and the mesh is offset by
    // it, smoothed, so the model stays framed at every stage of its fold. The
    // smoothing matters - snapping the offset each frame would make the sheet
    // slide about while it folds.
    // AND the fold has to REST ON THE TABLE. The sheet is laid flat, so a
    // facet's local z becomes world height - and folding drove that negative,
    // which put the whole model underneath the table plane: measured, world y
    // from -3.6 to -1.05 against a table at -1.55. All that stayed above the
    // surface was the top edge, which is why the only thing visible was a
    // couple of crease lines, and why they sat up near the cover. So the
    // lowest point of the folded form is pinned just above the table, at every
    // stage of every fold.
    let cx = 0, cz = 0, minH = 1e9;
    for (let i = 0; i < t * 3; i++) {
      cx += pos[i * 3]; cz += pos[i * 3 + 1];
      if (pos[i * 3 + 2] < minH) minH = pos[i * 3 + 2];
    }
    if (t) {
      cx /= t * 3; cz /= t * 3;
      const k = Math.min(1, (this._recenterFast ? 1 : 0.04));
      this._recenterFast = false;
      this._offX = (this._offX || 0) + (-cx - (this._offX || 0)) * k;
      this._offZ = (this._offZ || 0) + (-cz - (this._offZ || 0)) * k;
      const wantY = TABLE_Y + 0.03 - minH;
      this._offY = (this._offY == null) ? wantY : this._offY + (wantY - this._offY) * k;
      this._sheet.position.set(this._offX, this._offY, -this._offZ);
      this._creases.position.set(this._offX, this._offY, -this._offZ);
    }
  }

  _writeCreases() {
    const pos = this._creaseGeo.attributes.position.array;
    const col = this._creaseGeo.attributes.color.array;
    const p = new THREE.Vector3();
    let v = 0;
    const strength = this._crease;
    for (const f of this._facets) {
      if (!f.hinge || v >= 800) continue;
      // Mountain lighter, valley darker - the paper is stretched on one and
      // compressed in the other.
      const mountain = f.current >= 0;
      const lit = mountain ? 1.0 : 0.0;
      const amt = Math.min(1, Math.abs(f.current) / (80 * RAD)) * strength;
      for (let k = 0; k < 2; k++) {
        p.set(f.hinge[k][0], f.hinge[k][1], 0).applyMatrix4(this._facets[f.parent].matrix);
        pos[v * 3] = p.x; pos[v * 3 + 1] = p.y; pos[v * 3 + 2] = p.z;
        col[v * 3] = lit * amt * 0.95; col[v * 3 + 1] = lit * amt * 0.92; col[v * 3 + 2] = lit * amt * 0.85;
        // A valley is a dark line drawn over the lit paper, not an unlit one.
        if (!mountain) {
          col[v * 3] = 0.02 * amt; col[v * 3 + 1] = 0.02 * amt; col[v * 3 + 2] = 0.03 * amt;
        }
        v++;
      }
    }
    this._creaseGeo.attributes.position.needsUpdate = true;
    this._creaseGeo.attributes.color.needsUpdate = true;
    this._creaseGeo.setDrawRange(0, v);
  }

  _placeCamera(dt) {
    // A slow orbit is what reveals that the folding is three-dimensional at
    // all; from a fixed viewpoint a rigid fold reads as a flat animation.
    const ang = (this._time / ORBIT_PERIOD) * Math.PI * 2;
    const el = CAM_ELEVATION * RAD;
    const r = CAM_DIST * Math.cos(el);
    this._camera.position.set(Math.sin(ang) * r, CAM_DIST * Math.sin(el), Math.cos(ang) * r);
    // Aimed well ABOVE the paper, which pushes the sheet down into the lower
    // part of the frame and leaves the cover its own space up top - the
    // framing the brief asks for, and what stops the fold from happening
    // behind the artwork.
    this._camera.lookAt(0, 1.25, 0);
  }

  _pickModel(progress) {
    const avail = this._layout.models;
    if (this._forcedModel) return this._forcedModel;
    // progress -> complexity: the accordion opens and closes the track, the
    // crane and the hypar belong to its middle.
    const arc = Math.sin(Math.PI * clamp01(progress));
    const pool = arc < 0.35
      ? ['accordion', 'miura']
      : (arc < 0.7 ? ['miura', 'waterbomb', 'accordion'] : ['crane', 'hypar', 'waterbomb']);
    const usable = pool.filter((m) => avail.indexOf(m) >= 0);
    const list = usable.length ? usable : avail;
    let pick = list[(Math.random() * list.length) | 0];
    if (list.length > 1) { let n = 0; while (pick === this._modelName && n++ < 6) pick = list[(Math.random() * list.length) | 0]; }
    return pick;
  }

  // --- frame ---------------------------------------------------------------

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    if (paused) return;   // gel: the fold freezes mid-travel

    // ONE forced recompile, on the first frame. The material as CONSTRUCTED
    // compiles into a program that does not draw the paper - measured, 5190
    // bright pixels against 25914 after a recompile with nothing else changed,
    // the same geometry, camera, lights and material properties. Every earlier
    // test that appeared to show the sheet had forced a recompile as a side
    // effect of swapping material properties at runtime, which is why the
    // fault kept seeming to be somewhere else.
    //
    // The underlying reason is not established. What is established is that
    // the first compile of this material happens before it can produce a
    // working program and that asking for exactly one more fixes it for good.
    // Deliberately NOT on the first frame: at that point the material has not
    // compiled yet, so asking for an update is a no-op and the bad program is
    // still what gets built. It has to land after the first compile.
    this._frames = (this._frames || 0) + 1;
    if (this._frames === 8) {
      this._sheetMat.needsUpdate = true;
      // The table carries an injection of its own now, so it gets the same
      // treatment rather than waiting to exhibit the same fault.
      this._tableMat.needsUpdate = true;
    }

    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    this._aspectNow = this._camera && this._camera.aspect ? this._camera.aspect : 1.78;

    const director = profile.director;
    const bands = director && director.bands;
    const has = !!(bands && director.hasReliableGrid);
    const mid = has ? clamp01(bands.mid) : 0.5 + 0.28 * Math.sin(this._time * 0.19);
    const high = has ? clamp01(bands.high) : 0.45 + 0.3 * Math.sin(this._time * 0.27 + 1.3);
    const low = has ? clamp01(bands.low) : 0;
    const character = director && director.character;
    const dynamics = character && character.dynamics != null ? character.dynamics : 0.5;

    // bands.mid -> folding speed, +-20%. character.dynamics -> how much the
    // paper springs past its target at the end of a fold.
    this._speed = 1 + (mid - 0.5) * 2 * SPEED_SWING;
    this._overshoot = OVERSHOOT_BASE * lerp(0.5, 1.5, dynamics);
    this._crease = lerp(CREASE_MIN, CREASE_MAX, high);

    // bands.low -> a breath of air stirs ONE facet, damped over 1.5s.
    // Without an analysis the bands are flat, so the stir is self-started.
    let stir = false;
    if (has) { stir = low - this._lastLow > 0.10; this._lastLow = low; }
    else { this._stirNext = (this._stirNext || 3) - dt; if (this._stirNext <= 0) { this._stirNext = lerp(3, 7, Math.random()); stir = true; } }
    if (stir && this._facets.length > 1) {
      this._breathFacet = 1 + ((Math.random() * (this._facets.length - 1)) | 0);
      this._breath = 1;
      this._breathPhase = 0;
    }
    this._breath = Math.max(0, this._breath - dt * BREATH_DECAY);
    this._breathPhase += dt * 4.2;

    // --- the fold state machine --------------------------------------------
    // Every model change goes through the FLAT sheet. Cutting straight from
    // one folded state to another would violate rigidity, and it would be
    // visible immediately - facets would have to pass through each other.
    const accent = director ? director.accentPhrase : null;
    const phraseEdge = accent === 1 && this._lastAccent !== 1;
    this._lastAccent = accent;

    if (this._forcedFold != null) {
      this._phase = 'folding';
      this._phaseT = this._forcedFold;
    } else {
      const sp = this._speed;
      if (this._phase === 'folding') {
        this._phaseT = Math.min(1, this._phaseT + (dt * 1000 * sp) / this._foldTotalMs);
        if (this._phaseT >= 1 && phraseEdge) { this._phase = 'unfolding'; }
      } else if (this._phase === 'unfolding') {
        this._phaseT = Math.max(0, this._phaseT - (dt * 1000 * sp) / UNFOLD_MS);
        if (this._phaseT <= 0) { this._phase = 'flat'; this._flatT = 0; }
      } else if (this._phase === 'flat') {
        this._flatT += dt * 1000;
        if (this._flatT >= FLAT_HOLD_MS) {
          const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
          this._setModel(this._pickModel(progress));
          this._phase = 'folding';
          this._phaseT = 0;
        }
      }
    }

    this._updateMatrices(this._phaseT);
    this._writeGeometry();
    this._writeCreases();
    this._placeCamera(dt);
    this._backdropUniforms.uTime.value = this._time;
    this._backdropUniforms.uAspect.value = this._aspectNow;

    // palette -> the coloured face only. The reverse stays off-white, which is
    // what a sheet of origami paper is.
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const pal = profile.artworkPalette;
      const vivid = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors[0] : 0xd8664a;
      // Pulled hard toward white, with a floor under the result. The coloured
      // face is still PAPER: origami stock is a tint on white, not ink. Taken
      // raw from the palette it was dark enough to vanish against the table,
      // which is what made this visualizer look broken for a long time.
      this._accent.set(vivid).lerp(new THREE.Color(0xffffff), 0.55);
      const lum = 0.299 * this._accent.r + 0.587 * this._accent.g + 0.114 * this._accent.b;
      if (lum < 0.46) this._accent.multiplyScalar(0.46 / Math.max(lum, 0.02));

      // The room takes the cover's colour too, but at a fraction of it. These
      // are NORMALISED first - a palette entry can arrive at any brightness,
      // and scaling a dark one gives a dead room while a bright one would
      // wash the whole frame. Normalising to a known luminance and then
      // scaling to the constants above makes the dressing behave the same on
      // every cover, which is the point of it being dressing.
      const dom = pal && pal.dominantColors && pal.dominantColors.length ? pal.dominantColors[0] : vivid;
      const norm = (hex, level) => {
        const c = new THREE.Color(hex);
        const l = 0.299 * c.r + 0.587 * c.g + 0.114 * c.b;
        c.multiplyScalar(level / Math.max(l, 0.04));
        // Held back toward neutral: a tinted room, never a coloured one.
        return c.lerp(new THREE.Color(0x0e1014), 0.42);
      };
      this._roomTop.copy(norm(dom, ROOM_TINT_TOP));
      this._roomFloor.copy(norm(dom, ROOM_TINT_FLOOR));
      this._roomGlow.copy(norm(vivid, 0.016));
      this._tableUniforms.uPool.value.copy(norm(vivid, 0.075).lerp(new THREE.Color(0x14161c), 0.30));
    }
    // NOT DONE: the shared paper grain texture. Hung as three's own `map` it
    // multiplies diffuse by the texture raw and cost the sheet more than half
    // its brightness - measured, 17.7 against 38.6 - which on a dark table is
    // the difference between a sheet of paper and nothing at all. Dividing by
    // the texture's own mean does not recover it either: paper-texture.js
    // measures that mean at about 0.88, so the correction is a few percent
    // while the loss is a factor of two, which says the texture samples far
    // darker than its mean and the two are not the same problem. The grain is
    // the least load-bearing item on this visualizer's list, so it is left out
    // rather than left in at the cost of legibility.
    // The DRAWING BUFFER, not the CSS size. gl_FragCoord is in buffer pixels
    // and the renderer runs at a fraction of screen resolution, so feeding it
    // the CSS width mapped the whole sheet into a corner of NDC and left most
    // of it sitting inside the safe rectangle, dimmed to the floor.
    const bh = Math.max(1, getRenderBufferHeight());
    this._sheetUniforms.uRes.value.set(bh * this._aspectNow, bh);
  }

  dispose() {
    if (this._rendererRef && this._shadowWasEnabled != null) {
      this._rendererRef.shadowMap.enabled = this._shadowWasEnabled;
    }
    this._scene.remove(this._sheet, this._creases, this._table, this._backdrop,
                       this._key, this._fill, this._ambient);
    this._backdropGeo.dispose();
    this._backdropMat.dispose();
    this._sheetGeo.dispose();
    this._sheetMat.dispose();
    this._creaseGeo.dispose();
    this._creaseMat.dispose();
    this._tableGeo.dispose();
    this._tableMat.dispose();
    if (this._key.shadow && this._key.shadow.map) this._key.shadow.map.dispose();
  }
}
